//React
import React from "react";
import PropTypes from "prop-types";
import Loader from "../misc/loader";
import InfoTip from "../inputs/infoTip";
import HoverTip, { HoverTipScope } from "../inputs/hoverTip";
//Assets
import clockIcon from "../../assets/svgs/clock.svg";
//CSS
import "./metricsBar.scss";

/**
 * Metrics bar (ASB-4018): the white strip under the audience header that shows
 * the audience's size — Unique Users, Page Views — with an "Estimated" /
 * "Actual" qualifier and its explanation, exactly as the V2 Audience Builder
 * renders it. Extracted from AudienceBuilderV2.js for the signal and modeled
 * pages, where it replaces the legacy dark reach card in the right rail.
 *
 *   <MetricsBar
 *     metrics={[{ label: "Unique Users", value: "180K" }, { label: "Page Views", value: "3.1M" }]}
 *     qualifier="Actual"
 *     qualifierTip="Users and page views in the cohort over the last 30 days"
 *   />
 *
 * A metric with no value shows the "—" placeholder (nothing sized yet), and
 * `loading` swaps every value for a spinner. `actions` is the right-hand slot
 * (V2's lookback picker + Estimate Size; a timestamp on the modeled page);
 * a separator is drawn between the qualifier and the actions when both exist.
 *
 * `emptyMessage` says why there are no numbers ("model still being generated").
 * It gets its own line under the metrics (ASB-4036): in `actions` a sentence
 * squeezed the metrics until their labels wrapped. While it is shown and no
 * value is set, the "Estimated" / "Actual" word and its info tip are dropped,
 * also during a re-size (the signal slider): the tip describes numbers that
 * don't exist, and the message already says why. Without a message an empty
 * bar keeps both (V2 view page before its first metrics run).
 */
function MetricsBar({ metrics, loading, outdated, qualifier, qualifierTip, actions, emptyMessage, className }) {
	const noValues = (metrics ?? []).every((metric) => metric.value == null);
	const explainedEmpty = noValues && Boolean(emptyMessage);
	const shownQualifier = explainedEmpty ? undefined : qualifier;
	const hasQualifier = !explainedEmpty && Boolean(qualifier || qualifierTip);
	// The tiles' tips share one tooltip; a bar without any renders exactly as before.
	const Tips = (metrics ?? []).some((metric) => metric.tip) ? HoverTipScope : React.Fragment;
	return (
		<div className={["as-metrics-bar", className ?? ""].join(" ").trim()}>
			<div className="container">
				<div className="container--inner">
					<div className="as-metrics-bar__content">
						<Tips>
							<div className="as-metrics-bar__left">
								{(metrics ?? []).map((metric) => (
									<div
										key={metric.key ?? metric.label}
										className={[
											"as-metrics-bar__metric",
											metric.headline ? "as-metrics-bar__metric--headline" : "",
											metric.muted ? "as-metrics-bar__metric--muted" : "",
										]
											.join(" ")
											.trim()}
										data-metric={metric.key}
									>
										<span
											className="as-metrics-bar__value"
											title={metric.valueTitle}
										>
											{loading ? <Loader size="xs" /> : metric.value ?? "—"}
										</span>
										<span className="as-metrics-bar__label">
											{metric.tip ? (
												<HoverTip content={metric.tip}>{metric.label}</HoverTip>
											) : (
												metric.label
											)}
										</span>
									</div>
								))}
								{outdated && !loading ? (
									<span className="as-metrics-bar__outdated">
										<img
											src={clockIcon}
											alt=""
										/>{" "}
										Outdated
									</span>
								) : null}
							</div>
						</Tips>
						<div className="as-metrics-bar__right">
							{hasQualifier ? (
								<span className="as-metrics-bar__qualifier">
									{shownQualifier}
									{qualifierTip ? (
										<InfoTip
											content={qualifierTip}
											label={
												shownQualifier
													? `About ${shownQualifier.toLowerCase()} metrics`
													: "About these metrics"
											}
										/>
									) : null}
								</span>
							) : null}
							{hasQualifier && actions ? <div className="as-metrics-bar__separator" /> : null}
							{actions}
						</div>
					</div>
					{emptyMessage ? <p className="as-metrics-bar__message p4">{emptyMessage}</p> : null}
				</div>
			</div>
		</div>
	);
}

MetricsBar.propTypes = {
	/**
	 * label + already-formatted value; a null/undefined value renders the "—"
	 * placeholder. `tip` is help shown when the label is hovered or focused
	 * (ASB-4377 column `help`; the label is dotted-underlined to say so),
	 * `headline` renders the tile larger (a panel's headline number),
	 * `valueTitle` is the value's hover text, and `key` the React key when two
	 * tiles share a label.
	 */
	metrics: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.node,
			tip: PropTypes.string,
			headline: PropTypes.bool,
			/** the value is greyed (a ratio on too small a base) */
			muted: PropTypes.bool,
			valueTitle: PropTypes.string,
			key: PropTypes.string,
		})
	).isRequired,
	/** every value becomes a spinner */
	loading: PropTypes.bool,
	/** the numbers no longer match the criteria (V2: edited since the last estimate) */
	outdated: PropTypes.bool,
	/** "Estimated" / "Actual"; omit to show only the info icon (with `qualifierTip`) */
	qualifier: PropTypes.string,
	/** what the numbers mean, e.g. "30-day estimate based on historical data" */
	qualifierTip: PropTypes.string,
	/** right-hand slot */
	actions: PropTypes.node,
	/** why there are no numbers; its own line under the metrics, never in `actions` */
	emptyMessage: PropTypes.node,
	className: PropTypes.string,
};

export default MetricsBar;
